import {
  UseSelectState,
  UseSelectStateChangeOptions,
  UseSelectStateChangeTypes,
  useSelect,
} from 'downshift';
import React, {ChangeEvent, useState} from 'react';
import {Controller, UseFormMethods} from 'react-hook-form';
import styled from 'styled-components';
import DropdownSelect, {
  DropDownItem,
} from '../../../Forms/Inputs/DropdownSelect';
import {mediaMinDesktop} from '../../../constants/customMedia';
import {FormErrors} from '../types';
import {
  DropDownCountryTitle,
  DropDownSubline,
  FormSection,
  NestedSearchBarInput,
} from './styled';

import {GenericIconName} from '../../../Icon/IconName';
import {useGripsClientContext} from '../../../sharedContexts/GrpcClientContext';
import {ShippingDestination} from '../../assets/shippingDestinationList';
import {CountryRowItem} from './CountryRowItem';

interface Props {
  control: UseFormMethods['control'];
  labels: {
    required: string;
    country_select: string;
    country_select_search_placeholder: string;
    country_select_land: string;
    country_select_invalid: string;
  };
  errors: FormErrors;
  sections: ShippingDestination[];
}

export const CountrySection = ({control, labels, sections}: Props) => {
  const [countries, setCountries] = useState(sections);
  const [isSearchBarDirty, setIsSearchBarDirty] = useState(false);
  const {device} = useGripsClientContext();

  const filterCountriesBySearch = (event: ChangeEvent<HTMLInputElement>) => {
    return setCountries(
      sections.filter(
        c =>
          c.name.toLowerCase().search(event.target.value.toLowerCase()) !== -1,
      ),
    );
  };

  const filterOnFocusHandler = () => setIsSearchBarDirty(true);
  /*
      onBlur should be delayed slightly. Because when user selects
      from dropdown, onBlur is being called before selecting from dropdown.
      This way we reset the available countries after the user already selected his.
      */
  const filterOnBlurHandler = () =>
    setTimeout(() => setCountries(sections), 500);

  const CountryFilterComponent = (
    <>
      <NestedSearchBarInput
        placeholder={labels.country_select_search_placeholder}
        onChange={filterCountriesBySearch}
        onFocus={filterOnFocusHandler}
        onBlur={filterOnBlurHandler}
        isMobile={device === 'mobile'}
        {...(!isSearchBarDirty && {
          leftActionButton: {
            icon: GenericIconName.IcSearch,
            onPress: e => {
              e.preventDefault();
              return null;
            },
          },
        })}
      />
      <DropDownSubline>{labels.country_select_land}</DropDownSubline>
    </>
  );

  const handleSearchOverWrite = (
    _: UseSelectState<DropDownItem<unknown>>,
    {type, changes}: UseSelectStateChangeOptions<DropDownItem<unknown>>,
  ) => {
    const searchTypeEvents = [
      useSelect.stateChangeTypes.MenuBlur,
      useSelect.stateChangeTypes.MenuKeyDownSpaceButton,
    ] as UseSelectStateChangeTypes[];
    if (searchTypeEvents.includes(type)) {
      changes.isOpen = true;
    }
    return changes;
  };

  const validationRules = {
    required: true,
    validate: ({value = ''}) => value.length === 2,
  };

  return (
    <FormSection short data-testid="hcCountrySectionDropdownWrapper">
      <Controller
        name="country"
        control={control}
        rules={validationRules}
        shouldUnregister
        render={({onChange, value}, {invalid}) => (
          <DropdownOverwrite
            withBottomSheet={device === 'mobile'}
            bottomSheetAdditionalProps={{
              header: (
                <DropDownCountryTitle>
                  {labels.country_select}
                </DropDownCountryTitle>
              ),
              subHeader: CountryFilterComponent,
              afterClose: () => setCountries(sections),
            }}
            errorMessage={invalid ? labels.country_select_invalid : undefined}
            id={'country-select'}
            label={labels.country_select}
            items={countries.map(section => ({
              label: (
                <CountryRowItem
                  key={section.id}
                  {...section}
                  selected={section.id === value?.value?.id}
                />
              ),
              value: section.id,
            }))}
            onChange={onChange}
            value={value}
            data-testid="hcCountrySectionDropdown"
            searchBar={CountryFilterComponent}
            stateReducerOverWrite={handleSearchOverWrite}
          />
        )}
      />
    </FormSection>
  );
};

const DropdownOverwrite = styled(DropdownSelect)`
  @media ${mediaMinDesktop} {
    white-space: nowrap;
  }
  & > div {
    max-height: none;
    height: 264px;
  }
  & > div > ul {
    mask-image: none;
    -webkit-mask-image: none;
    background-color: #fff;
  }
`;
